import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import StyleOption from './StyleOption';
import { StyleState } from '../types/api';
import { ASSETS } from '../assets/assetMapping';
import { formatStyleDisplay, calculateStyleCount } from '../utils/filterUtils';

interface StyleDropdownProps {
  selectedStyle: StyleState;
  onStyleChange: (style: StyleState) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

// Style options with actual assets from Figma design
const styleOptions = {
  type: [
    { value: 'photorealistic', label: 'Photorealistic', image: ASSETS.PHOTOREALISTIC_STYLE },
    { value: 'cartoon', label: 'Cartoon', image: ASSETS.CARTOON_STYLE }
  ],
  ratio: [
    { value: 'vertical', label: 'Vertical', image: ASSETS.VERTICAL_RATIO },
    { value: 'horizontal', label: 'Horizontal', image: ASSETS.VERTICAL_RATIO } 
  ],
  scene: [
    { value: 'headshot', label: 'Headshot', image: ASSETS.FULL_BODY_SCENE, zoomLevel: 4, focusTop: 'head' },
    { value: 'mid-closeup', label: 'Mid close-up', image: ASSETS.FULL_BODY_SCENE, zoomLevel: 1.8, focusTop: 'mid' },
    { value: 'full-body', label: 'Full body', image: ASSETS.FULL_BODY_SCENE, zoomLevel: 1, focusTop: 'full' }
  ]
};

export default function StyleDropdown({ 
  selectedStyle, 
  onStyleChange, 
  isOpen, 
  onToggle, 
  onClose 
}: StyleDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: -1000, left: -1000, width: 160 });

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        const rect = buttonRef.current!.getBoundingClientRect();
        const viewport = {
          width: window.innerWidth,
          height: window.innerHeight
        };
        
        // Calculate centered position
        const dropdownWidth = 720; // min-w-[720px]
        const dropdownHeight = 200; // Estimated height
        
        // Center horizontally relative to button
        let left = rect.left + (rect.width / 2) - (dropdownWidth / 2);
        
        // Ensure dropdown doesn't go off screen
        if (left < 8) {
          left = 8;
        } else if (left + dropdownWidth > viewport.width - 8) {
          left = viewport.width - dropdownWidth - 8;
        }
        
        // Position below button
        let top = rect.bottom + 4;
        
        // If dropdown would go off screen, position above button
        if (top + dropdownHeight > viewport.height - 8) {
          const abovePosition = rect.top - dropdownHeight - 4;
          if (abovePosition >= 8) {
            top = abovePosition;
          } else {
            // Last resort: position with margin from bottom
            top = viewport.height - dropdownHeight - 8;
          }
        }
        
        setDropdownPosition({
          top: top,
          left: left,
          width: rect.width
        });
      };
      
      updatePosition();
      
      // Update position on scroll and resize
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  const handleOptionSelect = (category: keyof StyleState, value: string) => {
    const newStyle = { ...selectedStyle, [category]: value as any };
    onStyleChange(newStyle);
  };

  const styleCount = calculateStyleCount(selectedStyle);
  const displayText = formatStyleDisplay(selectedStyle);
  const isSelected = styleCount > 0;

  // Portal dropdown component
  const portalDropdown = isOpen && (
    <div 
      ref={dropdownRef}
      className="fixed bg-white rounded-2xl border border-blue-black-30 shadow-lg p-6 z-[9999] min-w-[720px]"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`
      }}
    >
      <div className="flex gap-8">
        {/* Type Section */}
        <div className="flex flex-col gap-4">
          <h3 className="font-inter font-semibold text-blue-black-90 text-[14px]">Type</h3>
          <div className="flex gap-4">
            {styleOptions.type.map((option) => (
              <StyleOption
                key={option.value}
                value={option.value}
                label={option.label}
                image={option.image}
                isSelected={selectedStyle.type === option.value}
                onClick={(value) => handleOptionSelect('type', value)}
              />
            ))}
          </div>
        </div>

        {/* Ratio Section */}
        <div className="flex flex-col gap-4">
          <h3 className="font-inter font-semibold text-blue-black-90 text-[14px]">Ratio</h3>
          <div className="flex gap-4">
            {styleOptions.ratio.map((option) => (
              <StyleOption
                key={option.value}
                value={option.value}
                label={option.label}
                image={option.image}
                isSelected={selectedStyle.ratio === option.value}
                onClick={(value) => handleOptionSelect('ratio', value)}
                rotateImage={option.value === 'horizontal'}
              />
            ))}
          </div>
        </div>

        {/* Scene Section */}
        <div className="flex flex-col gap-4">
          <h3 className="font-inter font-semibold text-blue-black-90 text-[14px]">Scene</h3>
          <div className="flex gap-4">
            {styleOptions.scene.map((option) => (
              <StyleOption
                key={option.value}
                value={option.value}
                label={option.label}
                image={option.image}
                isSelected={selectedStyle.scene === option.value}
                onClick={(value) => handleOptionSelect('scene', value)}
                zoomLevel={option.zoomLevel}
                focusTop={option.focusTop}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Dropdown Button */}
      <button
        ref={buttonRef}
        onClick={onToggle}
        className={`box-border flex gap-0.5 h-8 items-center justify-center px-4 py-1 relative rounded-lg shrink-0 transition-colors bg-white ${
          isOpen 
            ? 'bg-royal-30 border-royal' 
            : isSelected
            ? 'border-royal'
            : 'border-royal-30 hover:bg-gray-50'
        }`}
      >
        <div aria-hidden="true" className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
          isSelected ? 'border-royal' : 'border-royal-30'
        }`}/>
        <div className={`capitalize flex flex-col font-inter font-semibold justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-center text-nowrap ${
          isSelected ? 'text-royal' : 'text-blue-black-90'
        }`}>
          <p className="leading-[normal] whitespace-pre">
            {displayText}
          </p>
        </div>
      </button>

      {/* Portal dropdown rendered at document.body level */}
      {portalDropdown && createPortal(portalDropdown, document.body)}
    </div>
  );
}
